import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Spinner, Alert, Button } from 'reactstrap';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import CommonDataTable from '../../common/DataTable';
import { getSettingColumns } from '../../Components/Common/columns/Settings';
import { deleteSetting, getSettingWithFilter } from '../../slices/thunks';
import { setFilterParams } from '../../slices/settings/reducer';
import { useNavigate } from 'react-router-dom';

const SettingList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { settings, loading, error, filterParams } = useSelector(
    (state) => state.setting || {
      settings: { data: { data: [], totalCount: 0, currentPage: 1, totalPages: 1 } },
      filterParams: { currentPage: 1, perPage: 20 },
    }
  );

  // Fix data structure access based on the API response
  const settingsList = settings?.data?.data?.data || [];
  const totalCount = settings?.data?.data?.totalCount || 0;
  const currentPage = settings?.data?.data?.currentPage || 1;
  const totalPages = settings?.data?.data?.totalPages || 1;

  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmOpenForId, setConfirmOpenForId] = useState();

  useEffect(() => {
    const params = filterParams || { currentPage: 1, perPage: 20 };
    dispatch(getSettingWithFilter(params));
  }, [dispatch, filterParams]);

  const handleDelete = useCallback(async (settingId) => {
    try {
      await dispatch(deleteSetting({ id: settingId }));
      dispatch(getSettingWithFilter(filterParams));
      navigate("/settings");
    } catch (error) {
      console.error('Error deleting setting:', error);
    }
  }, [dispatch, filterParams, navigate]);

  const handleBulkDelete = useCallback(async () => {
    const settingIds = selectedRows.map((row) => row.id);
    if (settingIds.length > 0) {
      try {
        dispatch(getSettingWithFilter(filterParams));
        setSelectedRows([]);
      } catch (error) {
        console.error('Error in bulk delete:', error);
      }
    }
  }, [dispatch, selectedRows, filterParams]);

  const fetchData = useCallback((newFilterParams) => {
    // Update filter params in Redux state
    dispatch(setFilterParams(newFilterParams));
    // Fetch new data with updated params
    dispatch(getSettingWithFilter(newFilterParams));
  }, [dispatch]);

  const columns = getSettingColumns({
    onDeleteClick: handleDelete,
    confirmOpenForId,
    setConfirmOpenForId,
  });

  const handleAddButtonClick = () => {
    navigate('/settings/add-form');
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Settings" pageTitle="Settings" />
        </Container>
      </div>

      <div className="table-container">
        {loading && (
          <div className="text-center">
            <Spinner color="primary" />
            <p>Loading settings...</p>
          </div>
        )}

        {error && (
          <Alert color="danger">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {!loading && !error && (
          <CommonDataTable
            from="setting"
            data={settingsList}
            totalRows={totalCount}
            loading={loading}
            showAddButton={true}
            addOnClickMethod={handleAddButtonClick}
            columns={columns}
            checkboxEnabled={false}
            bulkDelete={handleBulkDelete}
            selectedRows={selectedRows}
            bulkSelected={(rows) => setSelectedRows(rows)}
            searchEnable={false}
            filterParams={filterParams || { currentPage: 1, perPage: 20 }}
            fetchData={fetchData}
            showExportButton={false}
            exportFileName="setting"
            currentPage={currentPage}
            totalPages={totalPages}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default SettingList;
